// @ts-check
// Formulário de "Enviar WhatsApp" (spec 2.0.0, fluxos.md seção 20.7; contrato 09 de fluxos, seção 17): número conectado,
// modelo aprovado no seletor agrupado da área de WhatsApp (contexto FLOW), o aviso de reclassificação do comunicado, o
// preenchimento das variáveis, quem recebe e a prévia. Modelo de marketing sem a permissão "Campanhas de WhatsApp" fica
// desabilitado com a explicação.
import { html } from 'htm/preact';
import { hasCurrentPermissionFlag } from '../../../data/metadata-store.js';
import { WhatsappTemplateGroupedPicker } from '../../whatsapp-campaigns/whatsapp-template-grouped-picker.js';
import { VariableMappingEditor } from '../../whatsapp-campaigns/variable-mapping-editor.js';
import { WhatsappMessagePreview } from '../../whatsapp-campaigns/whatsapp-message-preview.js';
import { TEMPLATE_VARIABLE_SOURCES, suggestVariableMappings, validateVariableMappings } from '../../../../../shared/whatsapp/template-variables.js';
import { FlowField, FlowNotice, fieldProblemText, flowProblemText } from '../flow-ui.js';
import { flowNodeReturnPath } from './email-node-form.js';
import { hasTranslation, t } from '../../../i18n/index.js';

/**
 * @typedef {import('../flow-node-panel.js').NodeFormProps} NodeFormProps
 * @typedef {import('../../../data/integrations.js').WhatsappTemplate} WhatsappTemplate
 * @typedef {import('../../../../../shared/whatsapp/template-variables.js').VariableMappings} VariableMappings
 */

/**
 * Valores da prévia sem um card de verdade: o texto fixo, ou o nome do que preenche entre colchetes ("[Primeiro nome]").
 * @param {VariableMappings | null | undefined} mappings @returns {Record<string, string>}
 */
export function previewValuesFromMappings(mappings) {
  /** @type {Record<string, string>} */
  const values = {};
  for (const part of /** @type {const} */ (['header', 'body'])) {
    for (const [name, mapping] of Object.entries(mappings?.[part] ?? {})) {
      const expression = String(mapping?.expression ?? '').trim();
      if (!expression) continue;
      const source = TEMPLATE_VARIABLE_SOURCES.find((item) => item.expression === expression);
      const key = source ? `whatsappCampaigns.variables.sources.${source.key}` : null;
      values[name] = key && hasTranslation(key) ? `[${t(key)}]` : expression.includes('{{') ? `[${expression.replace(/[{}]/g, '')}]` : expression;
    }
  }
  return values;
}

/** @param {NodeFormProps} props */
export function WhatsappNodeForm({ node, flowId, problems, lookupData, onConfig }) {
  const config = node.config;
  const channels = lookupData.channels ?? [];
  const templates = lookupData.whatsappTemplates;
  const template = (templates ?? []).find((candidate) => candidate.id === config.templateId) ?? null;
  const canUseMarketing = hasCurrentPermissionFlag('WHATSAPP_CAMPAIGNS');
  const missing = template ? validateVariableMappings(template, config.variableMappings).missing : [];
  const configProblem = problems.find((problem) => problem.code === 'WHATSAPP_CONFIG_INVALID' || problem.code === 'WHATSAPP_MARKETING_NEEDS_FLAG');
  const isUtility = template?.category === 'UTILITY';
  const createHref = `/campaigns/templates/whatsapp/new?from=${encodeURIComponent(flowNodeReturnPath(flowId, node.id))}`;

  return html`<div class="flow-node-form" data-testid="flow-whatsapp-form">
    <${FlowField} label=${t('flows.forms.whatsapp.channel')} htmlFor=${`flow-whatsapp-channel-${node.id}`} error=${fieldProblemText(problems, 'channelId')}>
      ${lookupData.channels === null
        ? html`<select class="text-input" disabled><option>${t('flows.common.loading')}</option></select>`
        : channels.length === 0
          ? html`<p class="flow-field-hint">${t('flows.forms.whatsapp.noChannels')} <a class="flow-link-button" href="/settings/channels">${t('flows.forms.whatsapp.goToChannels')}</a></p>`
          : html`<select id=${`flow-whatsapp-channel-${node.id}`} class="text-input" value=${config.channelId ?? ''}
              onChange=${(/** @type {Event} */ event) => onConfig({ channelId: /** @type {HTMLSelectElement} */ (event.currentTarget).value || null, templateId: null, variableMappings: {} })}>
              <option value="">${t('flows.forms.whatsapp.chooseChannel')}</option>
              ${channels.map((channel) => html`<option key=${channel.id} value=${channel.id} disabled=${channel.isActive === false}>
                ${channel.displayName ? `${channel.displayName} (${channel.handle})` : channel.handle}${channel.isActive === false ? ` ${t('flows.forms.whatsapp.disconnected')}` : ''}
              </option>`)}
            </select>`}
    <//>
    <${FlowField} label=${t('flows.forms.whatsapp.template')} error=${fieldProblemText(problems, 'templateId') ?? (configProblem ? flowProblemText(configProblem) : null)}>
      ${config.channelId
        ? html`<${WhatsappTemplateGroupedPicker} context="FLOW" selectedId=${config.templateId} createHref=${createHref}
            templates=${templates === null ? null : templates.filter((candidate) => candidate.channelId === config.channelId)}
            onRefresh=${lookupData.reload} label=${t('flows.forms.whatsapp.template')}
            disabledReason=${(/** @type {WhatsappTemplate} */ candidate) => (candidate.category === 'MARKETING' && !canUseMarketing ? t('flows.forms.whatsapp.marketingNeedsFlag') : null)}
            onSelect=${(/** @type {WhatsappTemplate} */ picked) => onConfig({ templateId: picked.id, variableMappings: suggestVariableMappings(picked) })} />`
        : html`<p class="flow-field-hint">${t('flows.forms.whatsapp.chooseChannelFirst')}</p>`}
    <//>
    ${template
      ? html`<span class="flow-chip" data-tone=${template.category === 'MARKETING' ? 'purple' : 'blue'}>${t(`flows.forms.whatsapp.categories.${template.category ?? 'OTHER'}`)}</span>`
      : null}
    ${isUtility ? html`<${FlowNotice} tone="warning" icon="IconAlertTriangle">${t('flows.forms.whatsapp.utilityNotice')}<//>` : null}
    ${template
      ? html`<div class="flow-field">
          <span class="flow-field-label">${t('flows.forms.whatsapp.variables')}</span>
          <${VariableMappingEditor} template=${template} mappings=${config.variableMappings ?? null} missing=${missing} idPrefix=${`flow-wa-${node.id}`}
            onChange=${(/** @type {VariableMappings} */ variableMappings) => onConfig({ variableMappings })} />
        </div>`
      : null}
    <${FlowField} label=${t('flows.forms.recipients.label')} htmlFor=${`flow-whatsapp-recipient-${node.id}`}>
      <select id=${`flow-whatsapp-recipient-${node.id}`} class="text-input" value=${config.recipientMode}
        onChange=${(/** @type {Event} */ event) => onConfig({ recipientMode: /** @type {HTMLSelectElement} */ (event.currentTarget).value })}>
        <option value="POINT_OF_CONTACT">${t('flows.forms.recipients.POINT_OF_CONTACT')}</option>
        <option value="ALL_GUARDIANS">${t('flows.forms.recipients.ALL_GUARDIANS')}</option>
      </select>
    <//>
    ${template
      ? html`<div class="flow-whatsapp-preview">
          <${WhatsappMessagePreview} template=${template} values=${previewValuesFromMappings(config.variableMappings)} title=${t('flows.forms.whatsapp.preview')} />
        </div>`
      : null}
    <${FlowNotice} icon="IconInfoCircle">${t('flows.forms.whatsapp.limitNotice')}<//>
  </div>`;
}
